import { json, type ActionFunctionArgs, type MetaFunction } from '@remix-run/cloudflare';
import type { LinksFunction } from '@remix-run/node';
import stylesheet from '../tailwind.css?url';
import { VYUKHeader } from '../components/header/vy-uk-header';
import { FooterSection } from '../sections/footer';
import { isRetryableNetworkError, retry } from '../network/retry';
import { useLocation } from '@remix-run/react';
import clsx from 'clsx';
import { images } from '../../constants/images';
import Sprite from '../sprites/sprite';
import { Seeker } from '../components/get-in-touch/get-in-touch';

export const links: LinksFunction = () => [{ rel: 'stylesheet', href: stylesheet }];

export const meta: MetaFunction = () => {
  return [
    { title: 'Past Events - Vihangam Yog UK' },
    {
      name: 'description',
      content: 'Past events and sessions from Vihangam Yog UK',
    },
  ];
};

export const action = async ({ request }: ActionFunctionArgs) => {
  try {
    const data = await request.formData();

    const name = data.get('name')?.toString();
    const interests = data.get('interests')?.toString();
    const email = data.get('email')?.toString();

    if (!name || !interests || !email) {
      return json('Not found', 404);
    }

    const interestsArr = JSON.parse(interests) as string[];

    const inquiryObj = {
      name,
      interests: interestsArr,
      email,
    } satisfies Seeker;

    try {
      const response = await retry(
        'post-inquiry',
        () =>
          fetch('https://api.vihangamyog.uk/api/inquiry', {
            method: 'POST',
            body: JSON.stringify(inquiryObj),
            headers: {
              'Content-Type': 'application/json',
            },
          }),
        {
          shouldRetryOnSuccess: isRetryableNetworkError,
        },
      );

      if (response.ok) {
        return json('OK', 200);
      }

      return Error('Bad request: ' + (await response.text()));
    } catch (e) {
      console.error(e);
      return json('Bad request', 500);
    }
  } catch (e) {
    return json('Not found', 404);
  }
};

const pastEvents = [
  {
    city: 'Leicester',
    name: 'Maher Centre',
    date: '09 August 2024',
    time: '3:30pm - 8:30pm',
    hasBooking: true,
    source:
      'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/VY%20Flyer%20-%20Leicester%20Aug%202024.pdf',
    id: 'leicester',
    tags: ['discourses', 'meditation', 'yajna'],
    eventUrl: 'https://www.facebook.com/events/346225531871289',
  },
  {
    city: 'London',
    name: 'Tooting Broadway',
    date: '10 August 2024',
    time: '5:30pm - 8:30pm',
    hasBooking: false,
    source:
      'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/Combined%20Flyer%20-%20UK%20Aug%202024.pdf',
    id: 'broadway',
    tags: ['discourses', 'meditation'],
    eventUrl: '',
  },
  {
    city: 'London',
    name: 'Bromley',
    date: '11 August 2024',
    time: '5:00pm - 8:00pm',
    hasBooking: false,
    source: 'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/VY%20UK%20Bromley.pdf',
    id: 'bromley',
    tags: ['discourses', 'meditation'],
    eventUrl: '',
  },
  {
    city: 'London',
    name: 'Slough',
    date: '15 August 2024',
    time: '4:00pm - 8:00pm',
    hasBooking: false,
    source:
      'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/Combined%20Flyer%20-%20UK%20Aug%202024.pdf',
    id: 'slough',
    tags: ['discourses', 'meditation'],
    eventUrl: '',
  },
  {
    city: 'Leeds',
    name: 'Leeds Temple',
    date: '17 August 2024',
    time: '3:00pm - 8:00pm',
    hasBooking: true,
    source:
      'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/VY%20UK%20Hawan%20Leeds.pdf',
    id: 'leeds',
    tags: ['discourses', 'meditation', 'yajna'],
    eventUrl: '',
  },
  {
    city: 'Bradford',
    name: undefined,
    date: '18 August 2024',
    time: '3:00pm - 8:00pm',
    hasBooking: false,
    source: 'https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/VY%20UK%20Bradford.pdf',
    id: 'bradford',
    tags: ['discourses', 'meditation', 'yajna'],
    eventUrl: '',
  },
] as const;

const PastEventsSection = () => {
  const location = useLocation();
  const currentEventSelection = location.hash ? location.hash.replace('#', '') : '';

  return (
    <section
      id="past-events-section"
      className="w-full mt-16 py-8 bg-blue-100 items-center flex flex-col text-center px-6 md:px-36 lg:px-60"
    >
      <h2 className="text-center font-display font-bold text-xl">August 2024</h2>
      <p className="mt-1 text-center">
        These events have concluded. Thank you to all who attended.
      </p>
      <div className="grid grid-cols-1 w-full sm:grid-cols-2 items-start gap-4 gap-y-2 whitespace-nowrap mt-2">
        {pastEvents.map((e) => (
          <div
            key={e.id}
            id={e.id}
            className={clsx(
              'mt-3 bg-white w-full rounded-md p-4 flex flex-col relative items-center sm:items-start gap-3 flex-wrap justify-center xs:justify-normal opacity-75',
              {
                'border-[3px] border-blue-400': currentEventSelection === e.id,
                'border-[3px] border-transparent': currentEventSelection !== e.id,
              },
            )}
          >
            <div className="flex flex-col items-center sm:items-start">
              <p className="text-lg font-bold leading-snug">
                {[e.name, e.city].filter((s) => s).join(', ')}
              </p>
              <p className="text-gray-500">
                {e.time} • {e.date}
              </p>
              <div className="flex flex-row flex-wrap justify-center sm:justify-normal items-center gap-2 mt-2">
                {e.tags.map((t) => (
                  <span
                    key={t}
                    className="bg-gray-100 uppercase text-[10px] px-2 py-1 rounded-sm tracking-wider"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-row items-start gap-3 flex-wrap w-full">
              <a
                href={e.source}
                target="_blank"
                rel="noreferrer"
                download
                className="w-full sm:w-[unset]"
              >
                <button className="w-full text-center sm:max-w-[unset] px-3 sm:px-5 py-3 sm:uppercase sm:tracking-widest text-sm sm:text-xs bg-blue-100 hover:bg-blue-100 active:bg-blue-50 rounded-md">
                  Download Brochure
                </button>
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const PastRadioSection = () => {
  return (
    <section className="relative mt-20 w-full flex flex-col sm:flex-row justify-center items-center">
      <img
        loading="lazy"
        src={images['Sarkar']}
        alt="Sant Naam Deo Ji Maharaj"
        className="object-cover object-[50%_60%] h-[480px] sm:h-[560px] md:h-[640px] w-full sm:max-w-[50%] select-none pointer-events-none"
      />
      <div className="flex flex-col w-full px-6 xs:px-9 sm:pt-20 m-auto mt-8 sm:mt-0 sm:m-[unset] sm:w-[50%] md:px-16 items-center sm:items-start">
        <Sprite name="quote" width="28" height="21" className="w-7 h-5" />
        <h2 className="font-display font-bold text-center sm:text-left text-2xl w-[90%] md:w-[500px] md:text-3xl lg:text-4xl mt-6 leading-tight">
          The silence within us opens up the door to the universe to begin conversing with
          us.
        </h2>
        <p className="font-bold italic font-display mt-7 sm:text-lg text-center sm:text-left">
          Sant Naam Deo Ji Maharaj
        </p>
        <p className="font-serif text-sm text-center sm:text-left sm:text-base">
          International Convenor of Vihangam Yog
        </p>
        <div className="border-b h-[1px] border-dashed border-gray-500 mt-9 mb-8 w-[80%] sm:w-full sm:max-w-[440px] md:max-w-[520px] border-g" />
        <div className="w-full md:max-w-[520px] px-0.5 py-0.5 rounded-xl flex flex-col items-center justify-center from-[#DBF4B2] to-[#C7E696] bg-gradient-to-r">
          <div className="w-full px-4 sm:px-12 rounded-xl py-7 flex flex-col items-center from-[#E6FFBE] to-[#F1FFDB] bg-gradient-to-r">
            <span className="flex flex-col sm:flex-row gap-2 sm:gap-0 items-center">
              <Sprite name="audio" width="34" height="26" className="h-5 md:w-8 md:h-7" />
              <span className="sm:ml-2 sm:text-nowrap sm:whitespace-nowrap italic font-display px-1 xs:px-4 sm:px-0 font-bold leading-tight text-center">
                Inner Wellbeing - with Naam Deo Ji
              </span>
            </span>
            <p className="font-serif text-center leading-tight mt-3">
              Past session on Rangoli Radio
              <br className="sm:block" />
              Saturday, the 25th of May, 2024
            </p>
            <a
              href="https://pub-24db26cc83d24526bdd25020f72d9ed8.r2.dev/Rangoli%20Radio%2025%20May%202024.pdf"
              download
              className="mt-6"
              target="_blank"
              rel="noreferrer"
            >
              <span className="cursor-pointer hover:underline text-gray-500 font-light text-xs font-serif uppercase tracking-wider">
                Learn more →
              </span>
            </a>
          </div>
        </div>
      </div>
      <div className="h-5/6 bg-gray-100 w-full top-1/2 -translate-y-1/2 left-0 absolute -z-10" />
    </section>
  );
};

export default function PastEvents() {
  return (
    <div className="w-full h-full relative">
      <VYUKHeader />
      <main className="xs:mt-12 mb-16 sm:mt-24 w-full">
        <div className="px-6 md:px-36 lg:px-60 py-8 flex flex-col items-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-center mb-4">
            Past Events
          </h1>
          <p className="text-lg text-center text-gray-600 font-serif max-w-3xl mx-auto">
            A collection of our previous sessions, workshops, and radio broadcasts. These
            events have concluded, but you can still access materials and learn more about
            our teachings.
          </p>
        </div>
        <PastEventsSection />
        <PastRadioSection />
      </main>
      <FooterSection showInquiry={false} />
    </div>
  );
}
